import { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../utils/apiConfig';

/**
 * Fetches public featured reviews and aggregate stats.
 * @param {number} maxReviews
 * @param {{ cacheBust?: boolean, enabled?: boolean }} options
 */
export default function useFeaturedReviews(maxReviews = 6, { cacheBust = true, enabled = true } = {}) {
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    const baseUrl = getApiUrl();
    const cacheQuery = cacheBust ? `&_t=${Date.now()}` : '';
    const fetchOpts = cacheBust
      ? { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
      : {};

    try {
      const [reviewsRes, statsRes] = await Promise.all([
        fetch(`${baseUrl}/api/reviews?limit=${maxReviews}&orderBy=helpfulVotes DESC${cacheQuery}`, fetchOpts),
        fetch(`${baseUrl}/api/reviews/stats${cacheBust ? `?_t=${Date.now()}` : ''}`, fetchOpts),
      ]);

      const reviewsCt = reviewsRes.headers.get('content-type') || '';
      if (reviewsCt.includes('application/json')) {
        const reviewsData = await reviewsRes.json();
        if (reviewsData.success) {
          setReviews(reviewsData.reviews || []);
        }
      }

      const statsCt = statsRes.headers.get('content-type') || '';
      if (statsCt.includes('application/json')) {
        const statsData = await statsRes.json();
        if (statsData.success) {
          setReviewStats(statsData.stats);
        }
      }
    } catch (err) {
      console.error('Error loading featured reviews:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [maxReviews, cacheBust, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { reviews, reviewStats, loading, error, reload: load };
}
